// frontend/src/components/postproduction/TicketMixChart.tsx
import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Paper,
  Button,
  Alert
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { PerformanceIntelligence } from '../../types/PPDash';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

interface TicketMixChartProps {
  performances: PerformanceIntelligence[];
}

// Color palette for different ticket types
const TICKET_COLORS: { [key: string]: string } = {
  earlybird: '#4caf50',
  generaladmission: '#2196f3',
  artsworker: '#ff9800',
  student: '#9c27b0',
  performer: '#e91e63',
  complimentary: '#795548',
  advanceticket: '#607d8b',
  other: '#9e9e9e'
};

const TicketMixChart: React.FC<TicketMixChartProps> = ({ performances }) => {
  // Debug: Log what we're receiving
  useEffect(() => {
    console.log('TicketMixChart received performances:', performances.length);
    console.log('Sample performance:', performances[0]);
    
    // Check how many performances have ticketMix data
    const withTicketMix = performances.filter(p => p.ticketMix && Object.keys(p.ticketMix).length > 0);
    console.log(`Performances with ticketMix data: ${withTicketMix.length}/${performances.length}`);
    
    // Log performances without ticketMix
    const withoutTicketMix = performances.filter(p => !p.ticketMix || Object.keys(p.ticketMix).length === 0);
    if (withoutTicketMix.length > 0) {
      console.log('Performances missing ticketMix:', withoutTicketMix.map(p => ({
        name: p.name,
        date: p.date,
        hasTicketMix: !!p.ticketMix,
        ticketMixKeys: p.ticketMix ? Object.keys(p.ticketMix) : []
      })));
    }
  }, [performances]);

  // Filter out performances that don't have any ticket mix data
  const validPerformances = useMemo(() => {
    return performances.filter(perf => {
      return perf.ticketMix && Object.keys(perf.ticketMix).length > 0;
    });
  }, [performances]);

  // Extract all unique ticket type keys from all performances
  const allTicketTypes = useMemo(() => {
    const typesSet = new Set<string>();
    validPerformances.forEach(perf => {
      Object.keys(perf.ticketMix).forEach(type => typesSet.add(type));
    });
    return Array.from(typesSet).sort();
  }, [validPerformances]);

  // Initialize all ticket types as visible
  const [visibleTypes, setVisibleTypes] = useState<{ [key: string]: boolean }>(() => {
    const initial: { [key: string]: boolean } = {};
    allTicketTypes.forEach(type => {
      initial[type] = true;
    });
    return initial;
  });

  // Update visible types when allTicketTypes changes
  useEffect(() => {
    const updated: { [key: string]: boolean } = {};
    allTicketTypes.forEach(type => {
      // Preserve existing visibility state if it exists
      updated[type] = visibleTypes[type] !== undefined ? visibleTypes[type] : true;
    });
    setVisibleTypes(updated);
  }, [allTicketTypes]);

  const handleToggle = (ticketType: string) => {
    setVisibleTypes(prev => ({
      ...prev,
      [ticketType]: !prev[ticketType]
    }));
  };

  const handleReset = () => {
    const reset: { [key: string]: boolean } = {};
    allTicketTypes.forEach(type => {
      reset[type] = true;
    });
    setVisibleTypes(reset);
  };

  const handleShowOnlyArtsWorker = () => {
    const artsWorkerOnly: { [key: string]: boolean } = {};
    allTicketTypes.forEach(type => {
      artsWorkerOnly[type] = type === 'artsworker';
    });
    setVisibleTypes(artsWorkerOnly);
  };

  // Prepare data for chart
  const chartData = useMemo(() => {
    return validPerformances.map(perf => {
      const dataPoint: any = {
        name: perf.name,
        date: new Date(perf.date).toLocaleDateString('en-GB', { 
          day: '2-digit', 
          month: 'short' 
        }),
        fullDate: perf.date // For sorting
      };

      // Add each ticket type's percentage
      Object.keys(perf.ticketMix).forEach(type => {
        if (visibleTypes[type]) {
          dataPoint[type] = perf.ticketMix[type].percentage;
          dataPoint[`${type}_count`] = perf.ticketMix[type].count; // For tooltip
        }
      });

      return dataPoint;
    }).sort((a, b) => new Date(a.fullDate).getTime() - new Date(b.fullDate).getTime());
  }, [validPerformances, visibleTypes]);

  // Format ticket type name for display
  const formatTicketTypeName = (key: string): string => {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 1.5, backgroundColor: 'background.paper' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            {label}
          </Typography>
          {payload.map((entry: any, index: number) => {
            const ticketType = entry.dataKey;
            const count = entry.payload[`${ticketType}_count`];
            return (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    backgroundColor: entry.color,
                    borderRadius: '2px'
                  }}
                />
                <Typography variant="caption">
                  {formatTicketTypeName(ticketType)}: {entry.value.toFixed(1)}% ({count} tickets)
                </Typography>
              </Box>
            );
          })}
        </Paper>
      );
    }
    return null;
  };

  const activeTypesCount = Object.values(visibleTypes).filter(v => v).length;
  const performancesWithoutData = performances.length - validPerformances.length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Ticket Mix Analysis
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {allTicketTypes.includes('artsworker') && (
            <Button
              onClick={handleShowOnlyArtsWorker}
              variant="outlined"
              size="small"
            >
              Arts Worker Only
            </Button>
          )}
          <Button
            startIcon={<RestartAltIcon />}
            onClick={handleReset}
            variant="outlined"
            size="small"
          >
            Show All
          </Button>
        </Box>
      </Box>

      {performancesWithoutData > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {performancesWithoutData} performance{performancesWithoutData > 1 ? 's' : ''} 
          {performancesWithoutData > 1 ? ' have' : ' has'} no ticket mix data and 
          {performancesWithoutData > 1 ? ' are' : ' is'} not shown in the chart.
        </Alert>
      )}

      {validPerformances.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Ticket Mix Data Available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            None of the performances have ticket mix data to display. 
            This could mean the data hasn't been calculated yet or there are no completed post-production reports.
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ p: 2, backgroundColor: 'background.paper' }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Filter Ticket Types:
            </Typography>
            <FormGroup row>
              {allTicketTypes.map(type => (
                <FormControlLabel
                  key={type}
                  control={
                    <Checkbox
                      checked={visibleTypes[type]}
                      onChange={() => handleToggle(type)}
                      size="small"
                      sx={{
                        color: TICKET_COLORS[type] || '#9e9e9e',
                        '&.Mui-checked': {
                          color: TICKET_COLORS[type] || '#9e9e9e',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {formatTicketTypeName(type)}
                    </Typography>
                  }
                />
              ))}
            </FormGroup>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {activeTypesCount === 0 ? 'Select at least one ticket type to display' : 
               activeTypesCount === allTicketTypes.length ? `Showing all ticket types (${validPerformances.length} performances)` :
               `Showing ${activeTypesCount} of ${allTicketTypes.length} ticket types (${validPerformances.length} performances)`}
            </Typography>
          </Box>

          {activeTypesCount === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <Typography color="text.secondary">
                Please select at least one ticket type to display the chart
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                <XAxis 
                  dataKey="date" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  tick={{ fill: '#aaa', fontSize: 12 }}
                />
                <YAxis 
                  label={{ value: 'Percentage (%)', angle: -90, position: 'insideLeft', fill: '#aaa' }}
                  tick={{ fill: '#aaa' }}
                  domain={[0, 100]}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  formatter={(value) => formatTicketTypeName(value)}
                />
                {allTicketTypes.map(type => 
                  visibleTypes[type] && (
                    <Bar 
                      key={type}
                      dataKey={type} 
                      stackId="a" 
                      fill={TICKET_COLORS[type] || '#9e9e9e'}
                    />
                  )
                )}
              </BarChart>
            </ResponsiveContainer>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default TicketMixChart;